import type { EditableFieldInfo } from 'components/shared/Blotter/Grid/blotter.model';
import { difference, uniq, uniqBy } from 'lodash-es';
import { onColumnStateChanged } from 'state/blotters/common/blotter.reducer.utils';
import {
  addOrPatchKeys,
  addOrReplaceKeys,
  patchKeysIfExists,
  prependToArray,
  keepKeys as removeMissingKeys,
} from 'state/utils/collection';
import type { LoadingStatus } from 'utils/status';

import { initialProductSearchState } from '.';
import type {
  ProductField,
  SecondaryMarketProductBlotterActions,
  SecondaryMarketProductsPreferenceStateLoaded,
} from './productSearch.actions';
import type {
  LoadableUnderlying,
  ProductSearchState,
  SecondaryMarketProduct,
  SecondaryMarketProductInternalUser,
} from './productSearch.model';

export function secondaryMarketQuotesReducer(
  state: ProductSearchState = initialProductSearchState,
  action: SecondaryMarketProductBlotterActions,
): ProductSearchState {
  switch (action.type) {
    case 'SECONDARY_MARKET_UNDERLYINGS_SEARCH_FAILED':
      return {
        ...state,
        underlyingsDetails: patchKeysIfExists(state.underlyingsDetails, action.keys, () => ({
          loadingStatus: 'error' as LoadingStatus,
        })),
      };
    case 'SECONDARY_MARKET_UNDERLYINGS_SEARCH_RECEIVED':
      return {
        ...state,
        underlyingsDetails: addOrReplaceKeys(
          state.underlyingsDetails,
          action.underlyings.map(u => [u.bcsKey, { ...u, loadingStatus: 'success' }]),
        ),
      };
    case 'SECONDARY_MARKET_UNDERLYINGS_SEARCH_REQUESTED':
      return {
        ...state,
        underlyingsDetails: addOrPatchKeys(
          state.underlyingsDetails,
          action.keys.map(key => [key, { bcsKey: key, loadingStatus: 'loading' } as LoadableUnderlying]),
        ),
      };
    case 'SECONDARY_MARKET_PRODUCT_CODE_SEARCH_CLEAR':
      return {
        ...state,
        searchingKeys: state.searchingKeys.filter(key => !action.codes.includes(key)),
        hasTechnicalError: false,
        notFoundKeys: state.notFoundKeys.filter(key => !action.codes.includes(key)),
      };
    case 'SECONDARY_MARKET_PRODUCT_CODE_SEARCH_REQUESTED':
      return {
        ...state,
        searchingKeys: action.keys,
        hasTechnicalError: false,
      };
    case 'SECONDARY_MARKET_PRODUCT_CODE_SEARCH_RECEIVED':
    case 'SECONDARY_MARKET_PRODUCT_CODE_SEARCH_RELOADED':
      const newIds = uniq(action.items.map(p => p.rowId));
      const itemsSearchedKeys = uniq(action.items.map(p => p.searchKey));
      const previous = state.items.filter(r => !newIds.includes(r.rowId));
      const uniqItems = uniqBy(action.items, 'rowId');
      return cleanUpUnderlyings({
        ...state,
        items: prependToArray(previous, uniqItems),
        searchingKeys: [],
        notFoundKeys: uniq([...state.notFoundKeys, ...difference(state.searchingKeys, itemsSearchedKeys)]),
        hasTechnicalError: false,
        errorMessage: undefined,
        timeout: undefined,
      });

    case 'SECONDARY_MARKET_PRODUCT_REFRESH_REQUESTED': {
      return {
        ...state,
        refreshingProductRowIds: prependToArray(state.refreshingProductRowIds, action.keys),
        refreshedProductRowIds: state.refreshedProductRowIds.filter(r => action.keys.includes(r)),
      };
    }
    case 'SECONDARY_MARKET_PRODUCT_REFRESH_FAILED': {
      return {
        ...state,
        refreshingProductRowIds: state.refreshingProductRowIds.filter(r => !action.keys.includes(r)),
        refreshedProductRowIds: state.refreshedProductRowIds.filter(r => !action.keys.includes(r)),
      };
    }
    case 'SECONDARY_MARKET_PRODUCT_REFRESH_RECEIVED':
      return cleanUpUnderlyings({
        ...state,
        items: state.items.map(item => action.items.find(i => i.rowId === item.rowId) ?? item) ?? null,
        refreshingProductRowIds: state.refreshingProductRowIds.filter(r => !action.keys.includes(r)),
        refreshedProductRowIds: prependToArray(state.refreshedProductRowIds, action.keys),
        searchingKeys: [],
        errorMessage: undefined,
        timeout: undefined,
        hasTechnicalError: false,
      });
    case 'SECONDARY_MARKET_PRODUCT_CODE_SEARCH_FAILED':
      return {
        ...state,
        searchingKeys: [],
        hasTechnicalError: true,
        errorMessage: action.error,
        timeout: action.timeout,
      };
    case 'SECONDARY_MARKET_PRODUCT_CODE_TYPE_CHANGED':
      return { ...state, codeType: action.newValue };
    case 'SECONDARY_MARKET_PRODUCT_COLUMNS_FETCHED':
      return { ...state, columns: action.columns };
    case 'SECONDARY_MARKET_PRODUCT_COLUMNS_FETCH_FAILED':
      return { ...state, columns: [], hasTechnicalError: true };
    case 'SECONDARY_MARKET_PRODUCT_REMOVE_ITEM':
      const items = state.items.filter(x => x.rowId !== action.rowId) ?? null;
      const searchedKeys = state.notFoundKeys.filter(key => key !== action.rowId);
      return cleanUpUnderlyings({ ...state, items, notFoundKeys: searchedKeys });
    case 'SECONDARY_MARKET_PRODUCT_CLEAR_ALL':
      return cleanUpUnderlyings({
        ...state,
        items: [],
        notFoundKeys: [],
        searchingKeys: [],
        underlyingsDetails: {},
      });
    case 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_GET':
      return { ...state, areBlotterPreferencesInError: false };
    case 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_LOADED':
      return onBlotterPreferenceStateLoaded(state, action);
    case 'SECONDARY_MARKET_PRODUCT_COLUMN_STATE_CHANGED':
      return onColumnStateChanged(state, action);
    case 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_LOADING_FAILED':
      return onPreferenceStateLoadingFailed(state);
    case 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_PERSISTENCE_FAILED':
      return onPreferenceStatePersistenceFailed(state);
    case 'SECONDARY_MARKET_PRODUCTS_VALUE_CHANGED':
      return {
        ...state,
        items: state.items.map(item => {
          if (action.change.rowId !== item.rowId) {
            // This isn't the item we care about - keep it as-is
            return item;
          }

          // Otherwise, this is the one we want - return an updated value
          return {
            ...item,
            [action.change.field]: action.change.value,
          };
        }),
      };
    case 'SECONDARY_MARKET_PRODUCTS_VALUE_CHANGED_REQUEST':
      return updateEditedStateItems(state, [action.change], () => ({
        status: 'loading',
      }));
    case 'SECONDARY_MARKET_PRODUCTS_VALUE_CHANGED_SET_EDITABLE_INFO':
      return {
        ...state,
        editState: {
          ...state.editState,
          items: {
            ...state.editState.items,
            [action.rowId]: {
              ...state.editState.items[action.rowId],
              [action.field]: action.info,
            },
          },
        },
      };
    case 'SECONDARY_MARKET_PRODUCTS_VALUE_CHANGED_DONE':
      return updateEditedStateItems(state, action.fields, f => ({
        status: f.hasBeenUpdated ? 'done' : 'failed',
        errors: action.errors,
        warnings: action.warnings,
      }));

    case 'SECONDARY_MARKET_PRODUCTS_VALUE_CHANGED_FAILED':
      return updateEditedStateItems(state, action.fields, _ => ({
        errors: action.errors,
        status: 'failed',
      }));
    case 'SECONDARY_MARKET_PRODUCT_SEARCH_FOCUSED':
      return { ...state, focusedProductId: action.id };
    default:
      return state;
  }

  function updateEditedStateItems<T extends ProductField>(
    state: ProductSearchState,
    changes: T[],
    transformChange: (f: T) => EditableFieldInfo,
  ) {
    const editedItems: typeof state.editState.items = {};
    changes.forEach(f => {
      editedItems[f.rowId] ??= { ...state.editState.items[f.rowId] };
      editedItems[f.rowId][f.field] = transformChange(f);
    });
    return {
      ...state,
      editState: { ...state.editState, items: { ...state.editState.items, ...editedItems } },
    };
  }

  function onBlotterPreferenceStateLoaded(
    state: ProductSearchState,
    action: SecondaryMarketProductsPreferenceStateLoaded,
  ): ProductSearchState {
    const blotterPreferenceState = action.blotterPreferenceState;
    if (blotterPreferenceState.columnState === undefined) {
      return {
        ...state,
        blotterPreferenceState,
        areBlotterPreferencesInError: false,
        canSaveBlotterPreferences: true,
      };
    }
    const isAllHidden = blotterPreferenceState.columnState.every(i => (i.hide ? i.hide : false));
    return {
      ...state,
      areBlotterPreferencesInError: false,
      canSaveBlotterPreferences: true,
      blotterPreferenceState: isAllHidden
        ? {
            ...blotterPreferenceState,
            columnState: blotterPreferenceState.columnState.map(i => ({
              ...i,
              hide: false,
            })),
          }
        : blotterPreferenceState,
    };
  }

  function onPreferenceStateLoadingFailed(state: ProductSearchState): ProductSearchState {
    return { ...state, areBlotterPreferencesInError: true, canSaveBlotterPreferences: false };
  }

  function onPreferenceStatePersistenceFailed(state: ProductSearchState): ProductSearchState {
    return { ...state, isPreferenceStatePersistenceFailed: false };
  }

  function getItemUnderlyings(item: SecondaryMarketProduct): string[] {
    const internalItem = item as SecondaryMarketProductInternalUser;
    return [...(internalItem.liveGreeks ?? []), ...(internalItem.closePriceGreeks ?? [])]
      .map(g => g.underlyingBcsKey)
      .filter(k => !!k) as string[];
  }

  function cleanUpUnderlyings(state: ProductSearchState): ProductSearchState {
    const underlyingsKeys = uniq(state.items.flatMap(i => getItemUnderlyings(i)));
    return { ...state, underlyingsDetails: removeMissingKeys(state.underlyingsDetails, underlyingsKeys) };
  }
}
